import { map, flatten, filter } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { type Flow_Answer, Flow_hasUnuploadedAnswers } from './Flow.answer'
import { Flow_fetchFullReport, Flow_findDescendantReports } from './Flow.Report'
import type { FlowReportStatus, FlowReport } from '../../../app-core/lib/flow/FlowReport'
import { IDB_flowAnswers, IDB_flowReports, IDB_doneReports, IDB_filesMeta } from '../IDB/IDB.dexie'
import type { OngoingReportChanges } from '../../../shared/domain/reports/ongoingReport.no-deps'
import { omitExtraAnswerFields } from '../../../shared/domain/flows/answer.no-deps'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import { Serv_clientWithLogger } from '../Serv/Serv.client'
import { Todo_doneReportCreate } from '../Todo/Todo.DoneReport'
import { User_get } from '../User/User.get'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import Errors from '../../../shared/domain/errors.no-deps'
import { type CatchError, getErrorMessage } from '../../../shared/lib/functional/error.no-deps'
import { getObjectsDifference } from '../../../shared/lib/functional/object.no-deps'
import { E2E_syncOngoingReportsStarted } from '../E2E/E2E.dispatch'
import { Cust_hasFeature } from '../Cust/Cust.hasFeature'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { syncReportsStartingFromLeavesToParents } from './Flow.sync-hierarchy'
import {
    Post_setReportPostInProgress,
    Post_setReportPostStatus,
    Post_setReportPostStepsCount,
    Post_setReportPostTitle,
} from '../Post/Post.reportPost'

import {
    Flow_dispatchNewAnswers,
    Flow_dispatchOpenStateChange,
    Flow_dispatchReportSynced,
    Flow_dispatchUnitDataChange,
} from './Flow.updateSubscriber'

import * as LaunchDarkly from '../Util/Util.launchdarkly'
import { isHttpRequestError } from '../../../server/client/httpRequest.no-deps'
import { File_getMetasToFetch } from '../File/File.Meta'
import { downloadOngoingReport } from './Flow.downloadCollaborativeReports'

export async function Flow_syncOngoingReports(logger: ClientLogger) {
    E2E_syncOngoingReportsStarted()

    const reportsByStartDate = IDB_flowReports().orderBy('startDate')
    if (LaunchDarkly.getFlagValue('parallel-ongoing-reports', true)) {
        const reports = await reportsByStartDate.toArray()
        return syncReportsStartingFromLeavesToParents(reports, logger)
    }

    /* Get the ids of all the flow reports, in reverse chronological order
         This way subtasks get updated before their parents */
    for (const reportId of await reportsByStartDate.reverse().primaryKeys()) {
        await Flow_syncOngoingReport(logger, reportId).catch(logger.error)
    }
}

export async function updateCachedUnitDataAttrs(
    logger: ClientLogger,
    reportClientId: ReportClientId,
    reportStatus: FlowReportStatus
) {
    if (!LaunchDarkly.getFlagValue('update-cached-unit-data', ENV.isTest)) {
        return
    }

    const flowReport = await downloadOngoingReport(logger, reportClientId, reportStatus)
    Flow_dispatchUnitDataChange(flowReport.id, flowReport.cachedUnitData)
}

export async function Flow_syncOngoingReport(
    logger: ClientLogger,
    reportClientId: ReportClientId,
    signature?: string,
    signedAtTime?: Date
) {
    logger.log('syncing ongoing report', { reportClientId })

    // Get the flow report
    let flowReport = await IDB_flowReports().get(reportClientId)

    // If the report id before move to multi-user reports, then ignore
    if (flowReport.collaborators == null) {
        logger.log('not syncing old report as ongoing report', { reportClientId })
        return
    }

    // Don't sync report if it's not meant to be synced
    if (flowReport?.uploadState === 'neverUpload') return

    // If the report no longer needs updates, then ignore
    if (flowReport?.doesNotNeedUpdates) return

    // If cancelled locally, tell the server
    flowReport = await uploadPendingCancel(logger, flowReport)

    // Upload answers
    flowReport = await uploadFlowAnswers(logger, flowReport)

    // Finalise if finished
    flowReport = await finalise(logger, flowReport, signature, signedAtTime)

    // Download changes from server
    flowReport = await syncChangesFromServer(logger, flowReport)

    // Upload invitations for other users to join report
    flowReport = await uploadInvites(logger, flowReport)

    flowReport = await markIfDoesNotNeedUpdates(logger, flowReport)

    // Update the UI to say that the report has finished syncing
    Flow_dispatchReportSynced(flowReport.id, flowReport.parentClientId)

    await updateCachedUnitDataAttrs(logger, reportClientId, flowReport.status)
}

async function uploadPendingCancel(
    logger: ClientLogger,
    flowReport: FlowReport<WorkflowReference>
): Promise<FlowReport<WorkflowReference>> {
    // If not pending cancel, then bail
    if (flowReport.uploadState !== 'pendingCancel') return flowReport

    // Don't need to send cancel to the server if it was never uploaded in the first place
    if (flowReport.serverId != null) {
        // Otherwise cancel the report
        await Serv_clientWithLogger(logger).cancelReport(flowReport.id)
    }

    // Update the report on disk so it doesn't get cancelled again
    return updateFlowReport(flowReport.id, { uploadState: 'uploaded' })
}

async function uploadInvites(logger: ClientLogger, flowReport: FlowReport<WorkflowReference>) {
    // If the report isn't open, don't bother
    if (flowReport.status !== 'open') return flowReport

    // If no invitations to upload then bail
    if (flowReport.collaborators.invited.length === 0) return flowReport

    // Upload invitations
    const { collaborators } = await Serv_clientWithLogger(logger).addReportCollaborators(
        flowReport.id,
        flowReport.collaborators.invited
    )

    // Update the report
    return updateFlowReport(flowReport.id, { collaborators: { accepted: collaborators, invited: [] } })
}

async function uploadFlowAnswers(logger: ClientLogger, flowReport: FlowReport<WorkflowReference>) {
    // If no longer working on report, don't bother to upload answers
    if (flowReport.status === 'cancelled' || flowReport.status === 'unassigned') return flowReport

    // Get all the answers not uploaded
    const answers = await IDB_flowAnswers()
        .where({
            report: flowReport.id,
            uploadState: 'pendingUpload',
        })
        .toArray()

    if (answers.length === 0) return flowReport

    const answerIds = answers.map((a) => a.id)
    const reportClientId = flowReport.id

    // Remove loopPaths until the server is ready for them
    if (!Cust_hasFeature('answers.loopPath')) {
        for (const answer of answers) delete answer.loopPath
    }

    try {
        // Upload them to the server
        await Serv_clientWithLogger(logger).createAnswers(reportClientId, answers.map(omitExtraAnswerFields))

        // Mark saved answers as uploaded
        await IDB_flowAnswers().where('id').anyOf(answerIds).modify({ uploadState: 'uploaded' })
    } catch (error) {
        if (shouldRetry(error)) throw error

        // Delete answers so they does get uploaded again
        logger.warn('cannot upload answers', { reportClientId, message: getErrorMessage(error) })
        await IDB_flowAnswers().bulkDelete(answerIds)

        // Continue without putting into pending finalisation
        return flowReport
    }

    // If the report has been complete, request the report is finalized later
    if (flowReport.status === 'completed') {
        return updateFlowReport(flowReport.id, { uploadState: 'pendingFinalisation' })
    }

    return flowReport
}

async function finalise(
    logger: ClientLogger,
    flowReport: FlowReport<WorkflowReference>,
    signature?: string,
    signedAtTime?: Date
) {
    // If not pending finalisation, then ignore
    if (flowReport.uploadState !== 'pendingFinalisation' && flowReport.uploadState !== 'pendingUploadAndFinalisation') {
        return flowReport
    }

    // If we don't think it's completed, then don't finalise
    if (flowReport.status !== 'completed') return flowReport

    const reportClientId = flowReport.id
    try {
        // Upload finalisation to server
        await Serv_clientWithLogger(logger).finalizeReport(reportClientId, flowReport.cachedUnitData, {
            doSendNotifications: true,
            signature,
            signedAtTime,
        })

        // Update flow report so we don't try to finalise again
        return updateFlowReport(flowReport.id, { uploadState: 'uploaded' })
    } catch (error) {
        // If finalisation should be retried, then rethrow
        if (shouldRetry(error)) throw error

        const reportIsCompleted = reportIsAlreadyComplete(error)
        const reportStatus = reportIsCompleted ? 'completed' : 'open'
        const warningMessage = reportIsCompleted ? 'report is already complete' : 'cannot finalize report'

        logger.warn(warningMessage, { reportClientId, message: getErrorMessage(error) })

        // Update the report
        flowReport = await updateFlowReport(flowReport.id, { status: reportStatus, uploadState: 'uploaded' })

        if (!reportIsCompleted) {
            // Update related entities
            await IDB_doneReports().where({ clientId: reportClientId }).delete()
            await Post_setReportPostInProgress(reportClientId, true)

            // Update any UI that depends on this report's open state
            Flow_dispatchOpenStateChange(reportClientId, 'open')
        }
    }

    return flowReport
}

function shouldRetry(error: CatchError) {
    if (isHttpRequestError(error)) {
        if (error.message === Errors.report.alreadyComplete) return false
        if (error.message === Errors.report.hasBeenCancelled) return false
        if (error.message === Errors.report.notReportCollaborator) return false
        if (error.message === Errors.report.reportIsIncomplete) return false
        if (error.message === Errors.report.reportIsInvalid) return false
    }
    return true
}

function reportIsAlreadyComplete(error: CatchError) {
    return getErrorMessage(error) === Errors.report.alreadyComplete
}

async function syncChangesFromServer(logger: ClientLogger, flowReport: FlowReport<WorkflowReference>) {
    // Download changes from the server
    const changes = await Serv_clientWithLogger(logger).getOngoingReportChanges(flowReport.id, flowReport.lastSyncTime)

    let updatedFlowReport: FlowReport<WorkflowReference> = {
        ...flowReport,
        lastSyncTime: changes.syncTime,
        collaborators: {
            ...flowReport.collaborators,
            accepted: changes.userCollaborators ?? flowReport.collaborators.accepted,
        },
    }

    const reportClientId = flowReport.id
    const originalState = updatedFlowReport.status
    const newState = getState(changes)

    // Completed
    if (originalState === 'open' && newState === 'completed') {
        logger.log('report has been completed', { reportClientId })
        updatedFlowReport.status = newState

        // Will be reassigned to more accurate time below in revaluateFlowSummary
        updatedFlowReport.completedDate = new Date()

        // Create a done item for the report
        await Todo_doneReportCreate(logger, {
            reportClientId: flowReport.id,
            title: flowReport.title,
            origin: flowReport.origin,
            isAction: flowReport.isAction,
            procedure: flowReport.procedure,
            workflow: flowReport.workflow,
            startDate: flowReport.startDate,
            endDate: new Date(),
            scheduledInstance: flowReport.scheduledInstance,
            teamDoneFor: flowReport.teamDoneFor,
            subjectUnitId: flowReport.subjectUnitId,
        })
    }

    // Cancelled
    if (originalState === 'open' && newState === 'cancelled') {
        logger.log('report has been cancelled', { reportClientId })
        updatedFlowReport.status = newState
        updatedFlowReport.cancelledDate = new Date()
    }

    // Unassigned
    if (originalState === 'open' && newState === 'unassigned') {
        logger.log('report has been unassigned', { reportClientId })
        updatedFlowReport.status = newState
        updatedFlowReport.unassignedDate = new Date()
    }

    // Reassigned
    if (originalState === 'unassigned' && newState === 'open') {
        logger.log('report has been reassigned', { reportClientId })
        updatedFlowReport.status = newState
        updatedFlowReport.unassignedDate = null
    }

    // If the report status has changed from a trigger
    if (changes.status != null && flowReport.reportStatusId !== changes.status) {
        updatedFlowReport.reportStatusId = changes.status
        await Post_setReportPostStatus(reportClientId, updatedFlowReport.reportStatusId)
    }

    // Save changes to report
    const partial = getObjectsDifference(flowReport, updatedFlowReport)
    updatedFlowReport = await updateFlowReport(reportClientId, partial)

    // If there's been a change in state
    if (originalState !== updatedFlowReport.status) {
        // Update report post to either show or hide
        await Post_setReportPostInProgress(reportClientId, updatedFlowReport.status !== 'completed')

        // Update UI if currently working on the report
        Flow_dispatchOpenStateChange(flowReport.id, updatedFlowReport.status)
    }

    // If there are new answers
    const hasNewAnswers = changes.answers != null && changes.answers.length > 0
    if (!hasNewAnswers) return updatedFlowReport

    // Save the answers
    const answers = changes.answers.map((answer): Flow_Answer => ({ ...answer, uploadState: 'uploaded' }))
    await IDB_flowAnswers().bulkPut(answers)

    // Filter file select answers
    const fileSelectAnswersIds = pipe(
        answers,
        filter((answer) => answer.type === 'files-select'),
        map((answer) => answer.value as FileId[]),
        flatten
    ) as FileId[]

    const fileIdsToFetch = await File_getMetasToFetch(fileSelectAnswersIds)

    if (fileIdsToFetch.length) {
        // Fetch meta data against file ids
        const filesMetaData = await Serv_clientWithLogger(logger).getFileMetaBulk({ fileIds: fileIdsToFetch })

        // Add file meta data to indexed db
        await IDB_filesMeta().bulkPut(filesMetaData.fileMeta)
    }

    // Update UI if currently working on the report
    Flow_dispatchNewAnswers(flowReport.id, answers)

    // Check if report's unit subject has been picked then revaluateFlowSummary otherwise return flowReport
    // This is to ensure that revaluateFlowSummary does not break on checking unit subject data as
    // it would not be available until user has picked unit subject
    if (!flowReport.subjectUnitId) {
        return updatedFlowReport
    } else {
        // With the new answers, revaluate the flow summary
        return revaluateFlowSummary(logger, updatedFlowReport)
    }
}

function getState(changes: OngoingReportChanges): FlowReportStatus {
    if (changes.state === 'completed') return 'completed'
    if (changes.state === 'cancelled') return 'cancelled'
    if (changes.userCollaborators != null) {
        return changes.userCollaborators.indexOf(User_get().id) === -1 ? 'unassigned' : 'open'
    }
    return null
}

async function revaluateFlowSummary(logger: ClientLogger, flowReport: FlowReport<WorkflowReference>) {
    const isAppendToReportTitleEnabled = LaunchDarkly.getFlagValue('append-to-report-title', ENV.isTest)
    const { flowSummary, title } = await Flow_fetchFullReport(logger, flowReport.id, {
        appendToReportTitle: isAppendToReportTitleEnabled,
    })
    const reportClientId = flowReport.id
    let updatedFlowReport = { ...flowReport }

    // Always prefer the completed date from flowSummary as more accurate
    if (flowSummary.validationFailures.length === 0) {
        updatedFlowReport.completedDate = flowSummary.completedDate
    }

    // If the report has been completed but the server hasn't explicitly completed it yet
    // i.e. the report is a subtask and has been completed but the server hasn't finalized
    if (
        flowReport.status === 'open' &&
        flowSummary.completedDate != null &&
        flowSummary.validationFailures.length === 0 &&
        flowReport.parentClientId != null
    ) {
        logger.log('report has been completed', { reportClientId })
        updatedFlowReport.status = 'completed'
        Flow_dispatchOpenStateChange(reportClientId, 'completed')
    }

    // If the report title has been changed
    if (title !== flowReport.title) {
        logger.log('report title has changed', { reportClientId, title: flowReport.title })

        await Post_setReportPostTitle(flowReport.id, title)
        await IDB_doneReports().where({ clientId: flowReport.id }).modify({ title })
    }

    // Update title
    updatedFlowReport.title = title

    // Save the flow report
    const partial = getObjectsDifference(flowReport, updatedFlowReport)
    updatedFlowReport = await updateFlowReport(reportClientId, partial)

    // Update the posts steps
    await Post_setReportPostStepsCount(flowReport.id, flowSummary.steps.length)

    return updatedFlowReport
}

async function markIfDoesNotNeedUpdates(logger: ClientLogger, flowReport: FlowReport<WorkflowReference>) {
    if (flowReport.status === 'open' || flowReport.parentClientId != null || flowReport.uploadState !== 'uploaded') {
        return flowReport
    }

    // Check there aren't answers still to upload
    // We could be in a state where the report is completed state
    // but we haven't uploaded the last of the answers yet
    const hasUnuploadedAnswers = await Flow_hasUnuploadedAnswers(flowReport.id)
    if (hasUnuploadedAnswers) return flowReport

    // Get all it's subtasks
    const descendants = await Flow_findDescendantReports(logger, flowReport.id)

    // Modify all with flag
    await IDB_flowReports()
        .where('id')
        .anyOf([flowReport.id, ...descendants])
        .modify((flowReport) => {
            flowReport.doesNotNeedUpdates = true
        })

    return { ...flowReport, doesNotNeedUpdates: true }
}

/**
 * Write partial changes to disk and re-reads so that the flow report is up to date
 */
async function updateFlowReport(reportClientId: ReportClientId, changes: Partial<FlowReport<WorkflowReference>>) {
    await IDB_flowReports().update(reportClientId, changes)
    return IDB_flowReports().get(reportClientId)
}
